import type { GroupedPanel } from "../workflows/workflowReducer";
import { Panel } from "../shared/interfaces/Switchgear";
import { convertUTP2PanelNoStringToNumber } from "./PanelNumberConversions";
/**
 * Function to be used to order Grouped Panels by Panel Number numerically. Panel Numbers with leading zeros are treated as negative numbers
 * and a Subset (Panel Number with a trailing 'A') must be ordered after the corresponding Panel if order is ascending.
 * A Panel Number must conform with the UTP2 device regular expression /^[0-9]{1,2}[A]?$/.
 * An example - Panel Numbers 1A,01,1,02,02A will be reordered in ascending order to 02,02A,01,1,1A.
 * @param groupedPanels The Grouped Panels
 * @param order The order (ASC or DESC)
 * @returns The Grouped Panels ordered numerically by Panel Number.
 */
export function orderPanelsByPanelNumberNumerically<T extends Panel | GroupedPanel>(
  groupedPanels: T[],
  order: "asc" | "desc",
) {
  const sortedPanels = [...groupedPanels];
  return sortedPanels.sort((a: T, b: T) => {
    return compareTwoPanelNumbers(a.panelNo, b.panelNo, order);
  });
}

/**
 * Function to be used within an array sort method to numerically compare two panel number strings.
 * Panel Numbers with leading zeros are treated as negative numbers
 * and a Subset (Panel Number with a trailing 'A') must be ordered after the corresponding Panel if order is ascending.
 * A Panel Number must conform with the UTP2 device regular expression /^[0-9]{1,2}[A]?$/.
 * An example - Panel Numbers 1A,01,1,02,02A will be reordered in ascending order to 02,02A,01,1,1A.
 * @param firstPanelNumber The first panel number
 * @param secondPanelNumber The second panel number
 * @param order The order (ASC or DESC)
 * @returns A negative number if firstPanelNumber occurs before secondPanelNumber; positive if the firstPanelNumber occurs after secondPanelNumber; 0 if they are equivalent or undefined.
 */
export function compareTwoPanelNumbers(
  firstPanelNumber: string | undefined,
  secondPanelNumber: string | undefined,
  order: "asc" | "desc",
) {
  if (firstPanelNumber && secondPanelNumber) {
    let firstPanelNoAsNumber = convertUTP2PanelNoStringToNumber(firstPanelNumber);
    let secondPanelAsNumber = convertUTP2PanelNoStringToNumber(secondPanelNumber);
    return order === "asc" ? firstPanelNoAsNumber - secondPanelAsNumber : secondPanelAsNumber - firstPanelNoAsNumber;
  } else return 0;
}
